import * as React from 'react';

import { cn } from '@/utils/cn';
import { cva, type VariantProps } from 'class-variance-authority';

const inputVariants = cva(
  'w-full border border-solid rounded-md shadow-xs transition-[color,box-shadow] focus-visible:outline-none disabled:cursor-not-allowed appearance-none selection:bg-primary-500 selection:text-white aria-invalid:border-error-500 aria-invalid:ring-error-500/20',
  {
    variants: {
      variant: {
        default:
          'bg-white border-neutral-300 text-neutral-950 placeholder:font-light placeholder:text-neutral-400 hover:bg-neutral-100 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:bg-neutral-50 disabled:text-neutral-950/40 disabled:border-neutral-300 disabled:hover:bg-neutral-50',
        borderless:
          'border-none rounded-none bg-transparent shadow-none focus-visible:ring-0 text-neutral-950 placeholder:font-light placeholder:text-neutral-400'
      },
      size: {
        sm: 'h-6 px-2 py-0 text-sm leading-[22px]',
        compact: 'h-8 px-2.5 py-1 text-sm leading-[22px]',
        md: 'h-9 px-3 py-1.5 text-[14px] leading-[22px]',
        lg: 'h-10 px-3 py-[7px] text-base leading-[24px]'
      },
      status: {
        default: '',
        error: 'border-error-500 hover:border-error-400 focus-visible:border-error-500 focus-visible:shadow-error-50',
        warning:
          'border-warning-500 hover:border-warning-400 focus-visible:border-warning-500 focus-visible:shadow-warning-50',
        success:
          'border-success-500 hover:border-success-400 focus-visible:border-success-500 focus-visible:shadow-success-50'
      }
    },
    compoundVariants: [
      {
        variant: 'borderless',
        status: ['error', 'warning', 'success'],
        className: 'border-none shadow-none'
      }
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
      status: 'default'
    }
  }
);

export type InputProps = Omit<React.ComponentProps<'input'>, 'size'> & VariantProps<typeof inputVariants>;

const Input = ({ className, type = 'text', variant, size, status, ...props }: InputProps) => {
  return (
    <input
      data-slot="input"
      type={type}
      className={cn(inputVariants({ variant, size, status, className }))}
      {...props}
    />
  );
};
Input.displayName = 'Input';

export { Input };
